<script setup lang="ts">
import ContentEditorFooter from '../../ContentEditorFooter.vue'
import ImageField from '../../ImageField.vue'
import { Loader2 } from 'lucide-vue-next'
import useOffer from '@/composables/offer'
import type { Offer } from '@/types/types'
import type { BrandAppOfferCard } from '../../../lib/brandAppContract'
import {
  OFFER_BUTTON_MAX_LENGTH,
  OFFER_DESCRIPTION_MAX_LENGTH,
  OFFER_TITLE_MAX_LENGTH,
  offerCardErrors,
  prefillOfferCard,
  type OfferField
} from '../../../lib/brandAppOffers'
import useContentEditing from '../../../composables/useContentEditing'

const props = defineProps<{ offers: Offer[]; card?: BrandAppOfferCard }>()
const emit = defineEmits<{ close: []; save: [card: BrandAppOfferCard] }>()
const { t } = useI18n()
const { fetchOffer } = useOffer()
const { fetchAllRewards } = useReward()
const selectedId = ref<number | null>(props.card?.offerId ?? null)
const source = ref<Offer>()
const draft = ref<BrandAppOfferCard>(
  props.card
    ? { ...props.card }
    : {
        offerId: 0,
        rewardId: 0,
        image: '',
        title: '',
        description: '',
        ctaLabel: t('pages.loyaltySupports.webapp.offers.defaultButton')
      }
)
const image = ref(props.card?.image ?? '')
const loading = ref(false)
const loadFailed = ref(false)
let request = 0
const options = computed(() => props.offers.map((offer) => ({ value: offer.id, label: offer.name })))
const presentation = computed(() => ({ ...draft.value, image: image.value }))
const selectionValid = computed(
  () => selectedId.value !== null && props.offers.some((offer) => offer.id === selectedId.value)
)
const busy = computed(() => selectionValid.value && (loading.value || loadFailed.value))
const attempt = ref<'hide' | 'show' | null>(null)
const fieldErrors = computed<Partial<Record<OfferField | 'offer', string>>>(() => {
  if (!attempt.value) return {}
  if (!selectionValid.value) return { offer: 'offerRequired' }
  const errors = offerCardErrors(presentation.value)
  return attempt.value === 'show' ? errors : errors.title ? { title: errors.title } : {}
})
const errorOf = (field: OfferField | 'offer') => {
  const key = fieldErrors.value[field]
  return key ? t(`pages.loyaltySupports.webapp.offers.${key}`) : ''
}

const { setDraft } = useContentEditing()
watch(presentation, (item) => setDraft({ kind: 'offer', item: { ...item } }), { immediate: true })

const load = async () => {
  const version = ++request
  const id = selectedId.value
  source.value = undefined
  loadFailed.value = false
  loading.value = false
  if (id === null || !selectionValid.value) return
  loading.value = true
  try {
    const offer = await fetchOffer(id)
    if (version !== request) return
    let reward = offer.reward
    if (!reward) {
      const rewards = await fetchAllRewards()
      if (version !== request) return
      if (!rewards) throw new Error('Rewards unavailable')
      reward = rewards.find((item) => item.id === offer.rewardId)
    }
    source.value = { ...offer, reward }
    if (draft.value.offerId !== id) {
      draft.value = prefillOfferCard(source.value, t('pages.loyaltySupports.webapp.offers.defaultButton'))
      image.value = draft.value.image
    } else {
      draft.value = { ...draft.value, publicId: offer.publicId, rewardId: offer.rewardId }
    }
  } catch {
    if (version === request) loadFailed.value = true
  } finally {
    if (version === request) loading.value = false
  }
}
watch(selectedId, load, { immediate: true })
onBeforeUnmount(() => {
  request++
})

const isVisible = ref(props.card?.isVisible !== false)

const save = () => {
  attempt.value = isVisible.value ? 'show' : 'hide'
  if (busy.value || !source.value || Object.keys(fieldErrors.value).length) return
  emit('save', {
    ...presentation.value,
    isVisible: isVisible.value,
    title: draft.value.title.trim(),
    description: draft.value.description.trim(),
    ctaLabel: draft.value.ctaLabel.trim()
  })
}
</script>

<template>
  <div class="flex flex-col">
    <form id="brand-app-offer-form" class="flex min-w-0 flex-col gap-5 px-6 pb-6" @submit.prevent>
      <div class="flex flex-col gap-2">
        <Label>{{ t('pages.loyaltySupports.webapp.offers.choose') }} *</Label>
        <ComboboxSelect
          v-model="selectedId"
          :options="options"
          :search-threshold="0"
          :placeholder="t('pages.loyaltySupports.webapp.offers.selectPlaceholder')"
          :search-placeholder="t('pages.loyaltySupports.webapp.offers.search')"
          :empty-message="t('pages.loyaltySupports.webapp.offers.noResults')"
        />
        <p v-if="errorOf('offer')" class="text-destructive text-sm">{{ errorOf('offer') }}</p>
      </div>

      <p v-if="loading" role="status" class="text-muted-foreground flex items-center gap-2 text-sm">
        <Loader2 class="size-4 animate-spin" />{{ t('pages.loyaltySupports.webapp.offers.loadingDetails') }}
      </p>
      <div v-if="loadFailed" class="flex flex-col items-start gap-2">
        <p role="alert" class="text-destructive text-sm">
          {{ t('pages.loyaltySupports.webapp.offers.detailsFailed') }}
        </p>
        <Button variant="outline" size="sm" @click="load">{{ t('pages.loyaltySupports.webapp.offers.retry') }}</Button>
      </div>

      <template v-if="selectionValid">
        <div class="flex flex-col gap-2">
          <Label for="brandApp.offer.image">{{ t('pages.loyaltySupports.webapp.offers.image') }} *</Label>
          <ImageField id="brandApp.offer.image" v-model="image" :aspect-ratio="4 / 3" crop />
          <p v-if="errorOf('image')" class="text-destructive text-sm">{{ errorOf('image') }}</p>
        </div>

        <div class="flex flex-col gap-2">
          <Label for="brand-app-offer-title">{{ t('pages.loyaltySupports.webapp.offers.title') }} *</Label>
          <Input id="brand-app-offer-title" v-model="draft.title" :maxlength="OFFER_TITLE_MAX_LENGTH" />
          <p v-if="errorOf('title')" class="text-destructive text-sm">{{ errorOf('title') }}</p>
        </div>

        <div class="flex flex-col gap-2">
          <Label for="brand-app-offer-description">{{ t('pages.loyaltySupports.webapp.offers.description') }}</Label>
          <Textarea
            id="brand-app-offer-description"
            v-model="draft.description"
            class="min-h-28"
            :maxlength="OFFER_DESCRIPTION_MAX_LENGTH"
          />
          <p v-if="errorOf('description')" class="text-destructive text-sm">{{ errorOf('description') }}</p>
        </div>

        <div class="flex flex-col gap-2">
          <Label for="brand-app-offer-button">{{ t('pages.loyaltySupports.webapp.offers.button') }} *</Label>
          <Input id="brand-app-offer-button" v-model="draft.ctaLabel" :maxlength="OFFER_BUTTON_MAX_LENGTH" />
          <p v-if="errorOf('ctaLabel')" class="text-destructive text-sm">{{ errorOf('ctaLabel') }}</p>
        </div>
      </template>
    </form>

    <ContentEditorFooter v-model:visible="isVisible" :disabled="busy" @cancel="emit('close')" @save="save" />
  </div>
</template>
